import React, { useState } from 'react';
import { Paper, TextField, Grid, Typography, Button, Box, Alert, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

// Fuentes disponibles para selección
const AVAILABLE_FONTS = [
  { name: 'Inter', value: 'Inter', category: 'sans-serif' },
  { name: 'Roboto', value: 'Roboto', category: 'sans-serif' },
  { name: 'Open Sans', value: 'Open Sans', category: 'sans-serif' },
  { name: 'Lato', value: 'Lato', category: 'sans-serif' },
  { name: 'Montserrat', value: 'Montserrat', category: 'sans-serif' },
  { name: 'Poppins', value: 'Poppins', category: 'sans-serif' },
  { name: 'Raleway', value: 'Raleway', category: 'sans-serif' },
  { name: 'Nunito', value: 'Nunito', category: 'sans-serif' },
  { name: 'Source Sans Pro', value: 'Source Sans Pro', category: 'sans-serif' },
  { name: 'Ubuntu', value: 'Ubuntu', category: 'sans-serif' },
  { name: 'League Spartan', value: 'League Spartan', category: 'sans-serif' },
  { name: 'Playfair Display', value: 'Playfair Display', category: 'serif' },
  { name: 'Merriweather', value: 'Merriweather', category: 'serif' },
  { name: 'Lora', value: 'Lora', category: 'serif' },
  { name: 'Crimson Text', value: 'Crimson Text', category: 'serif' },
  { name: 'Georgia', value: 'Georgia', category: 'serif' },
  { name: 'Times New Roman', value: 'Times New Roman', category: 'serif' },
  { name: 'Arial', value: 'Arial', category: 'sans-serif' },
  { name: 'Helvetica', value: 'Helvetica', category: 'sans-serif' },
  { name: 'System UI', value: 'system-ui', category: 'sans-serif' },
];

// Función para obtener la fuente principal de un array de fuentes
const getPrimaryFont = (fontArray) => {
  if (!fontArray || !Array.isArray(fontArray) || fontArray.length === 0) {
    return 'Inter'; // Valor por defecto
  }
  // Retornar la primera fuente que no sea un fallback del sistema
  const systemFallbacks = ['system-ui', 'sans-serif', 'serif', 'monospace'];
  const primary = fontArray.find(font => !systemFallbacks.includes(font)) || fontArray[0] || 'Inter';
  
  // Si la fuente no está en la lista disponible, retornarla de todas formas
  // para que el select pueda manejarla (aunque no se mostrará en el dropdown)
  return primary;
};

// Función para construir el array de fuentes con fallbacks
const buildFontArray = (primaryFont) => {
  if (!primaryFont) return [];
  
  // Encontrar la categoría de la fuente
  const fontInfo = AVAILABLE_FONTS.find(f => f.value === primaryFont);
  const fallback = fontInfo?.category || 'sans-serif';
  
  // Si es una fuente del sistema, retornar solo ella
  if (primaryFont === 'system-ui' || primaryFont === 'Arial' || primaryFont === 'Helvetica' || primaryFont === 'Georgia' || primaryFont === 'Times New Roman') {
    return [primaryFont, fallback];
  }
  
  // Para fuentes de Google Fonts, incluir fallbacks
  return [primaryFont, fallback];
};

const FontsTab = () => {
  const { t } = useTranslation();
  const { config, handleChange, isFieldConfigurable, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);

  const handleSaveFonts = async () => {
    try {
      setSaving(true);
      
      // Preparar updates para todos los campos de fuentes que sean configurables
      const updates = {};
      
      // Fuentes
      if (isFieldConfigurable('theme.fonts.sans')) {
        updates['theme.fonts.sans'] = config.theme.fonts.sans;
      }
      if (isFieldConfigurable('theme.fonts.display')) {
        updates['theme.fonts.display'] = config.theme.fonts.display;
      }

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      
      // Recargar configuración
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving fonts:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  const hasEditableFonts = isFieldConfigurable('theme.fonts.sans') || isFieldConfigurable('theme.fonts.display');

  if (!hasEditableFonts) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>{t('fonts') || 'Fuentes'}</Typography>
        </Grid>
        {isFieldConfigurable('theme.fonts.sans') && (() => {
          const currentFont = getPrimaryFont(config.theme.fonts.sans);
          const bodyFonts = AVAILABLE_FONTS.filter(font => font.category === 'sans-serif' || font.value === 'system-ui');
          const fontExists = bodyFonts.some(f => f.value === currentFont);
          const fontsToShow = fontExists 
            ? bodyFonts 
            : [...bodyFonts, { name: currentFont, value: currentFont, category: 'sans-serif' }];
          
          return (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('body_font') || 'Fuente del Cuerpo'}</InputLabel>
                <Select
                  value={currentFont}
                  onChange={(e) => {
                    const fontArray = buildFontArray(e.target.value);
                    handleChange('theme.fonts.sans', fontArray);
                  }}
                  label={t('body_font') || 'Fuente del Cuerpo'}
                >
                  {fontsToShow.map((font) => (
                    <MenuItem key={font.value} value={font.value}>
                      <Box sx={{ fontFamily: font.value === 'system-ui' ? 'system-ui' : `"${font.value}"` }}>
                        {font.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          );
        })()}
        {isFieldConfigurable('theme.fonts.display') && (() => {
          const currentFont = getPrimaryFont(config.theme.fonts.display);
          const fontExists = AVAILABLE_FONTS.some(f => f.value === currentFont);
          const fontsToShow = fontExists 
            ? AVAILABLE_FONTS 
            : [...AVAILABLE_FONTS, { name: currentFont, value: currentFont, category: 'serif' }];
          
          return (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('title_font') || 'Fuente de Títulos'}</InputLabel>
                <Select
                  value={currentFont}
                  onChange={(e) => {
                    const fontArray = buildFontArray(e.target.value);
                    handleChange('theme.fonts.display', fontArray);
                  }}
                  label={t('title_font') || 'Fuente de Títulos'}
                >
                  {fontsToShow.map((font) => (
                    <MenuItem key={font.value} value={font.value}>
                      <Box sx={{ fontFamily: font.value === 'system-ui' ? 'system-ui' : `"${font.value}"` }}>
                        {font.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          );
        })()}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSaveFonts} disabled={saving}>
              {saving ? (t('saving') || 'Guardando...') : (t('save_fonts') || 'Guardar Fuentes')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FontsTab;

